<script lang="ts" module>
  /**
   * Photo support is discovered lazily: the first POST photo-url that answers
   * 503 (R2 not configured) hides the photo block for the rest of the session.
   */
  let photoSupported = $state(true);
</script>

<script lang="ts">
  import { apiFetch, APIError } from '../lib/api';
  import { navigate } from '../lib/router';
  import { errorMessage } from '../lib/errors';
  import { fmtDay, isDateKey, todayKey } from '../lib/format';
  import { plans } from '../lib/plans.svelte';
  import { toast } from '../lib/toast.svelte';
  import { preparePhoto, PHOTO_MAX_BYTES } from '../lib/photo';
  import type { Day, DayMeal, MealSelection, PhotoUrl } from '../lib/types';
  import Icon from '../components/Icon.svelte';
  import LoadState from '../components/LoadState.svelte';
  import OptionRow from '../components/OptionRow.svelte';

  let { params = {} }: { params?: Record<string, string> } = $props();

  let date = $derived(params.date ?? '');
  let mealNo = $derived(Number(params.mealNo));
  let validRoute = $derived(isDateKey(date) && Number.isInteger(mealNo) && mealNo >= 1 && mealNo <= 5);

  let day = $state<Day | null>(null);
  let loading = $state(false);
  let error = $state<string | null>(null);
  let busy = $state(false);
  let uploading = $state(false);
  let fileInput = $state<HTMLInputElement | null>(null);

  let loadedFor = '';

  async function load(): Promise<void> {
    const target = date;
    loading = true;
    error = null;
    try {
      const d = await apiFetch<Day>(`/api/days/${target}`);
      if (target === date) { day = d; loadedFor = target; }
    } catch (err) {
      if (target === date) error = errorMessage(err);
    } finally {
      if (target === date) loading = false;
    }
  }

  $effect(() => {
    if (!validRoute) return;
    if (loadedFor !== date) {
      day = null;
      void load();
    }
  });

  let meal = $derived<DayMeal | null>(day?.meals.find((m) => m.mealNo === mealNo) ?? null);
  let planTitle = $derived(plans.titleOf(day?.planId));
  let backPath = $derived(validRoute && date !== todayKey() ? `/paiva/${date}` : '/');

  const SECTION_COLOR: Record<string, string> = {
    h: 'var(--viisi-c-carb)',
    p: 'var(--viisi-c-prot)',
    r: 'var(--viisi-c-fat)',
  };

  function selectedIds(m: DayMeal): string[] {
    return m.sections.flatMap((s) => s.items.filter((i) => i.selected).map((i) => i.id));
  }

  function selectedCount(sectionId: string): number {
    const s = meal?.sections.find((x) => x.id === sectionId);
    return s ? s.items.filter((i) => i.selected).length : 0;
  }

  async function put(body: MealSelection, okMessage?: string): Promise<void> {
    if (busy) return;
    busy = true;
    try {
      day = await apiFetch<Day>(`/api/days/${date}/meals/${mealNo}`, { method: 'PUT', body });
      if (okMessage) toast.show(okMessage);
    } catch (err) {
      toast.show(errorMessage(err, 'Tallennus epäonnistui'));
    } finally {
      busy = false;
    }
  }

  function toggleItem(id: string): void {
    if (!meal) return;
    const ids = selectedIds(meal);
    const next = ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
    void put({ itemIds: next, vegDone: meal.vegDone });
  }

  function toggleVeg(): void {
    if (!meal) return;
    void put({ itemIds: selectedIds(meal), vegDone: !meal.vegDone });
  }

  function randomize(): void {
    if (!meal) return;
    const ids: string[] = [];
    for (const s of meal.sections) {
      if (!s.items.length) continue;
      ids.push(s.items[Math.floor(Math.random() * s.items.length)].id);
    }
    void put({ itemIds: ids, vegDone: meal.vegDone }, 'Arvottu! Paina uudelleen, jos ei nappaa.');
  }

  /* ---- photo ---- */

  function pickPhoto(): void {
    if (uploading || busy) return;
    fileInput?.click();
  }

  async function onFile(e: Event): Promise<void> {
    const input = e.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    uploading = true;
    try {
      const prepared = await preparePhoto(file);
      if (prepared.blob.size > PHOTO_MAX_BYTES) {
        toast.show('Kuva on liian suuri (enintään 10 Mt).');
        return;
      }
      let ticket: PhotoUrl;
      try {
        ticket = await apiFetch<PhotoUrl>(`/api/days/${date}/meals/${mealNo}/photo-url`, {
          method: 'POST',
          body: { contentType: prepared.contentType, fileSize: prepared.blob.size },
        });
      } catch (err) {
        if (err instanceof APIError && err.status === 503) {
          photoSupported = false;
          toast.show('Kuvat eivät ole käytössä tässä ympäristössä.');
          return;
        }
        throw err;
      }
      const res = await fetch(ticket.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': prepared.contentType },
        body: prepared.blob,
      });
      if (!res.ok) throw new Error('Kuvan lähetys epäonnistui.');
      day = await apiFetch<Day>(`/api/days/${date}/meals/${mealNo}/photo`, { method: 'PUT', body: { key: ticket.key } });
      toast.show('Kuva lisätty');
    } catch (err) {
      toast.show(errorMessage(err, 'Kuvan lähetys epäonnistui.'));
    } finally {
      uploading = false;
    }
  }

  async function deletePhoto(): Promise<void> {
    if (uploading || busy) return;
    uploading = true;
    try {
      day = await apiFetch<Day>(`/api/days/${date}/meals/${mealNo}/photo`, { method: 'DELETE' });
      toast.show('Kuva poistettu');
    } catch (err) {
      toast.show(errorMessage(err, 'Kuvan poisto epäonnistui.'));
    } finally {
      uploading = false;
    }
  }
</script>

<div class="topbar">
  <button type="button" class="back" aria-label="Takaisin" onclick={() => navigate(backPath)}>
    <Icon name="back" />
  </button>
  <div class="grow">
    <div class="sub">{validRoute ? `${fmtDay(date)} · ${planTitle}` : ''}</div>
    <h1>{meal?.name ?? `Ateria ${Number.isInteger(mealNo) ? mealNo : ''}`}</h1>
  </div>
  {#if meal}
    <button type="button" class="btn small ghost" disabled={busy} onclick={randomize}>
      <Icon name="shuffle" />Arvo
    </button>
  {/if}
</div>

{#if !validRoute}
  <LoadState error="Virheellinen osoite." onretry={() => navigate('/')} />
{:else if !day}
  <LoadState {loading} {error} onretry={load} />
{:else if !meal}
  <LoadState error="Ateriaa ei löydy." onretry={() => navigate(backPath)} />
{:else}
  {#if photoSupported}
    <div class="photo-wrap">
      <button
        type="button"
        class="photo"
        class:has={Boolean(meal.photoUrl)}
        disabled={uploading}
        onclick={pickPhoto}
        aria-label={meal.photoUrl ? 'Vaihda annoksen kuva' : 'Lisää kuva annoksesta'}
      >
        {#if meal.photoUrl}
          <img src={meal.photoUrl} alt="Annoksen kuva" />
          <span class="photo-lbl on-img">{uploading ? 'Lähetetään kuvaa...' : 'Kuva lisätty · vaihda'}</span>
        {:else}
          <div>
            <Icon name="camera" /><br />
            <span class="photo-lbl">{uploading ? 'Lähetetään kuvaa...' : 'Lisää kuva annoksesta'}</span>
          </div>
        {/if}
      </button>
      {#if meal.photoUrl}
        <button type="button" class="photo-del" aria-label="Poista kuva" title="Poista kuva" disabled={uploading} onclick={deletePhoto}>
          <Icon name="trash" />
        </button>
      {/if}
      <input
        bind:this={fileInput}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        capture="environment"
        hidden
        onchange={onFile}
      />
    </div>
  {/if}

  {#each meal.sections as s (s.id)}
    {@const n = selectedCount(s.id)}
    <div class="sec">
      <div class="sec-head">
        <h3><i style="background:{SECTION_COLOR[s.kind] ?? 'var(--viisi-surface-3)'}"></i>Valitse {s.label.toLowerCase()}</h3>
        <span class="hint">{n === 0 ? 'ei valintaa' : n === 1 ? '1 valittu' : `${n} valittu, jaettu tasan`}</span>
      </div>
      <div class="opts" aria-busy={busy}>
        {#each s.items as item (item.id)}
          <OptionRow {item} split={n > 1} disabled={busy} ontoggle={toggleItem} />
        {/each}
      </div>
      {#if n > 1}
        <div class="split-note">{n} vaihtoehtoa valittu: määrät jaettu tasan (1/{n} kustakin).</div>
      {/if}
    </div>
  {/each}

  {#if meal.vegTargetG !== null}
    <div class="sec">
      <div class="sec-head"><h3><i class="veg-i"></i>Vihannekset tai kasvikset</h3></div>
      <button
        type="button"
        class="veg"
        class:on={meal.vegDone}
        role="checkbox"
        aria-checked={meal.vegDone}
        disabled={busy}
        onclick={toggleVeg}
      >
        <span class="box"><Icon name="check" /></span>
        <span>
          <span class="name">Vihanneksia tai kasviksia</span><br />
          <span class="vsub">esim. kurkku, tomaatti, paprika, kaalit, porkkana</span>
        </span>
        <span class="g num">{meal.vegTargetG} g</span>
      </button>
    </div>
  {/if}

  <div class="tip">Useamman vaihtoehdon valinta jakaa grammat tasan. Kuivapaino ensin, kypsäpaino pienellä.</div>
{/if}

<style>
  .topbar {
    position: sticky;
    top: 0;
    z-index: 5;
    background: linear-gradient(var(--viisi-ground) 80%, transparent);
    padding: 18px 0 12px;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .topbar h1 { font-size: 26px; font-weight: 800; letter-spacing: -0.01em; flex: 1; }
  .topbar .sub { color: var(--viisi-ink-2); font-size: 13px; }
  .grow { flex: 1; min-width: 0; }
  .back {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: var(--viisi-surface-2);
    display: grid;
    place-items: center;
    flex: none;
  }
  .back :global(svg) { width: 18px; height: 18px; }

  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 11px 16px;
    border-radius: 12px;
    font-weight: 700;
    font-size: 14px;
    background: var(--viisi-accent);
    color: var(--viisi-accent-ink);
  }
  .btn.ghost { background: var(--viisi-accent-soft); color: var(--viisi-accent); }
  .btn.small { padding: 7px 12px; font-size: 13px; border-radius: var(--viisi-radius-sm); }
  .btn :global(svg) { width: 16px; height: 16px; }
  .btn:disabled { opacity: 0.6; cursor: progress; }

  .photo-wrap { position: relative; }
  .photo {
    width: 100%;
    height: 140px;
    border-radius: var(--viisi-radius);
    border: 1.5px dashed var(--viisi-line);
    background: var(--viisi-surface);
    display: grid;
    place-items: center;
    color: var(--viisi-ink-2);
    font-size: 13px;
    font-weight: 600;
    text-align: center;
    gap: 6px;
    overflow: hidden;
    position: relative;
  }
  .photo :global(svg) { width: 28px; height: 28px; stroke: var(--viisi-ink-3); }
  .photo.has { border-style: solid; background: var(--viisi-photo-bg); color: var(--viisi-ink); }
  .photo img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .photo-lbl.on-img {
    position: relative;
    z-index: 1;
    padding: 6px 12px;
    border-radius: 999px;
    background: var(--viisi-nav-bg);
    color: var(--viisi-ink);
    font-size: 12px;
  }
  .photo:disabled { cursor: progress; }
  .photo-del {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: var(--viisi-nav-bg);
    color: var(--viisi-bad);
    display: grid;
    place-items: center;
    box-shadow: var(--viisi-shadow);
  }
  .photo-del :global(svg) { width: 16px; height: 16px; stroke: var(--viisi-bad); }

  .sec { margin-top: 18px; }
  .sec-head { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 8px; gap: 8px; }
  .sec-head h3 { font-size: 15px; font-weight: 700; display: flex; align-items: center; gap: 8px; }
  .sec-head h3 i { width: 10px; height: 10px; border-radius: 3px; flex: none; }
  .veg-i { background: var(--viisi-c-veg); }
  .sec-head .hint { font-size: 12px; color: var(--viisi-ink-2); white-space: nowrap; }
  .opts {
    background: var(--viisi-surface);
    border-radius: var(--viisi-radius);
    box-shadow: var(--viisi-shadow);
    overflow: hidden;
  }
  .split-note {
    font-size: 12px;
    color: var(--viisi-accent);
    background: var(--viisi-accent-soft);
    padding: 8px 12px;
    border-radius: var(--viisi-radius-sm);
    margin-top: 8px;
    font-weight: 600;
  }

  .veg {
    display: grid;
    grid-template-columns: 26px 1fr auto;
    gap: 10px;
    align-items: center;
    width: 100%;
    text-align: left;
    padding: 13px 14px;
    background: var(--viisi-surface);
    border-radius: var(--viisi-radius);
    box-shadow: var(--viisi-shadow);
  }
  .veg .box {
    width: 22px;
    height: 22px;
    border-radius: 7px;
    border: 1.5px solid var(--viisi-ink-3);
    display: grid;
    place-items: center;
  }
  .veg .box :global(svg) { width: 14px; height: 14px; display: none; }
  .veg.on .box { background: var(--viisi-c-veg); border-color: var(--viisi-c-veg); color: var(--viisi-c-veg-ink); }
  .veg.on .box :global(svg) { display: block; }
  .veg .name { font-weight: 600; }
  .veg .vsub { font-size: 12px; color: var(--viisi-ink-2); font-weight: 400; }
  .veg .g { font-family: var(--viisi-font-display); font-weight: 800; font-size: 16px; }
  .veg:disabled { cursor: progress; }

  .tip { font-size: 12.5px; color: var(--viisi-ink-3); margin-top: 16px; }
</style>
